import { useState, useEffect, useCallback } from 'react';

// Custom hook for invoice pagination and filtering
export const useInvoicePagination = () => {
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    invoiceNumber: '',
    user: '',
    subject: '',
    status: '',
  });
  
  // Debounced filter values
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  
  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 400);
    
    return () => clearTimeout(timer);
  }, [filters]);
  
  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [debouncedFilters]);
  
  // Pagination handlers
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);
  
  const handleRowsPerPageChange = useCallback((e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  }, [rowsPerPage]);
  
  const handleCustomRowsInputChange = useCallback((e) => {
    setCustomRowsInput(e.target.value);
  }, []);
  
  const handleCustomRowsInputBlur = useCallback(() => {
    const value = parseInt(customRowsInput);
    if (value && value > 0) {
      setRowsPerPage(value);
      setPage(0);
    }
    setCustomRowsActive(false);
  }, [customRowsInput]);
  
  // Filter handlers
  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);
  
  const clearFilters = useCallback(() => {
    setFilters({
      invoiceNumber: '',
      user: '',
      subject: '',
      status: '',
    });
  }, []);
  
  // Get pagination params for API calls
  const getPaginationParams = useCallback(() => {
    const params = {
      page: page + 1,
      page_size: rowsPerPage,
    };
    
    // Add filters if they have values
    if (debouncedFilters.invoiceNumber) {
      params.invoice_number = debouncedFilters.invoiceNumber;
    }
    if (debouncedFilters.user) {
      params.user = debouncedFilters.user;
    }
    if (debouncedFilters.subject) {
      params.subject = debouncedFilters.subject;
    }
    if (debouncedFilters.status) {
      params.status = debouncedFilters.status;
    }
    
    return params;
  }, [page, rowsPerPage, debouncedFilters]);
  
  return {
    // Pagination state
    page,
    rowsPerPage,
    customRowsInput,
    customRowsActive,
    
    // Filter state
    filters,
    debouncedFilters,
    
    // Handlers
    handlePageChange,
    handleRowsPerPageChange,
    handleCustomRowsInputChange,
    handleCustomRowsInputBlur,
    handleFilterChange,
    clearFilters,
    
    // Utilities
    getPaginationParams,
  };
};
